import type { User, UserGroup } from '@gmn-portal/backend';
import type { ReactNode } from 'react';
import type { EntityPermissionOverviewEntry } from '@/core/api';
import { useIntl } from '@/core/i18n/use-intl';
import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderRow,
  TableRow,
} from '@/shared/components/ui';

type UsersAccessTableProps = {
  users: User[];
  userGroupsById: Map<string, UserGroup>;
  overviewByUserId: Map<string, EntityPermissionOverviewEntry>;
  isOverviewLoading?: boolean;
  emptyLabel?: string;
  renderAction: (params: {
    user: User;
    group: UserGroup | null;
    readAllowed: boolean | null;
    updateAllowed: boolean | null;
  }) => ReactNode;
  showUpdate?: boolean;
};

const getUserLabel = (user: User) => {
  const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  return fullName !== '' ? fullName : user.email || user.id;
};

const resolveGroup = (user: User, userGroupsById: Map<string, UserGroup>) => {
  const group = user.userGroup;
  if (!group) return null;
  if (typeof group === 'string') {
    return userGroupsById.get(group) ?? null;
  }
  return group as UserGroup;
};

export function UsersAccessTable({
  users,
  userGroupsById,
  overviewByUserId,
  isOverviewLoading = false,
  emptyLabel,
  renderAction,
  showUpdate = true,
}: UsersAccessTableProps) {
  const { t } = useIntl();

  const resolveGroupLabel = (group: UserGroup | null) => {
    const labelKey = `portalRoles.${group?.key ?? ''}`;
    const translated = group?.key ? t(labelKey as unknown as never) : '';
    return group?.key && translated !== labelKey ? translated : (group?.key ?? '-');
  };

  const renderAccessBadge = (allowed: boolean | null) => (
    <Badge variant={allowed === null ? 'secondary' : allowed ? 'outline-solid' : 'secondary'}>
      {allowed === null
        ? t('permissions.accessLoading')
        : allowed
          ? t('permissions.accessGranted')
          : t('permissions.accessDenied')}
    </Badge>
  );

  if (users.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        {isOverviewLoading
          ? t('permissions.accessLoading')
          : emptyLabel || t('permissions.noUsers')}
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableHeaderRow>
          <TableHead className="py-2">{t('permissions.user')}</TableHead>
          <TableHead className="py-2">{t('permissions.group')}</TableHead>
          <TableHead className="py-2 text-center">{t('permissions.read')}</TableHead>
          {showUpdate && (
            <TableHead className="py-2 text-center">{t('permissions.update')}</TableHead>
          )}
          <TableHead className="py-2 text-right">{t('permissions.action')}</TableHead>
        </TableHeaderRow>
      </TableHeader>
      <TableBody>
        {users.map((user) => {
          const group = resolveGroup(user, userGroupsById);
          const accessEntry = overviewByUserId.get(user.id);
          const readAllowed = isOverviewLoading ? null : (accessEntry?.read.allowed ?? false);
          const updateAllowed = isOverviewLoading ? null : (accessEntry?.update.allowed ?? false);

          return (
            <TableRow key={user.id} data-testid={`view-as-user-${user.id}`}>
              <TableCell className="py-2">
                <div className="flex flex-col min-w-0">
                  <span className="font-medium truncate">{getUserLabel(user)}</span>
                  {user.email && (
                    <span className="text-xs text-muted-foreground truncate">{user.email}</span>
                  )}
                </div>
              </TableCell>
              <TableCell className="py-2">
                <Badge variant="secondary">{resolveGroupLabel(group)}</Badge>
              </TableCell>
              <TableCell className="py-2 text-center">{renderAccessBadge(readAllowed)}</TableCell>
              {showUpdate && (
                <TableCell className="py-2 text-center">
                  {renderAccessBadge(updateAllowed)}
                </TableCell>
              )}
              <TableCell className="py-2 text-right">
                {renderAction({ user, group, readAllowed, updateAllowed })}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
